import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { universityService, University } from 'services/universityService';
import { memberService } from 'services/memberService';
import { authService } from 'services/authService';
import * as S from './SearchMain.styled';

function SearchMain() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'email' | 'password'>('email');

  // 아이디 찾기 관련 state
  const [universities, setUniversities] = useState<University[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [selectedUniversityId, setSelectedUniversityId] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [studentId, setStudentId] = useState('');
  const [name, setName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 비밀번호 찾기 관련 state
  const [passwordEmail, setPasswordEmail] = useState<string>('');
  const [passwordVerificationCode, setPasswordVerificationCode] = useState<string>('');
  const [isRequestingPasswordCode, setIsRequestingPasswordCode] = useState<boolean>(false);
  const [passwordRemainingTime, setPasswordRemainingTime] = useState<number>(0);
  const [isPasswordCodeSent, setIsPasswordCodeSent] = useState<boolean>(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [isVerifyingPassword, setIsVerifyingPassword] = useState<boolean>(false);
  const passwordTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadUniversities();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 비밀번호 찾기 타이머
  useEffect(() => {
    if (passwordRemainingTime > 0) {
      passwordTimerRef.current = setTimeout(() => {
        setPasswordRemainingTime(passwordRemainingTime - 1);
      }, 1000);
    } else if (passwordRemainingTime === 0 && isPasswordCodeSent) {
      setIsPasswordCodeSent(false);
    }

    return () => {
      if (passwordTimerRef.current) {
        clearTimeout(passwordTimerRef.current);
      }
    };
  }, [passwordRemainingTime, isPasswordCodeSent]);

  useEffect(() => {
    if (searchText) {
      const filtered = universities.filter(uni =>
        uni.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredUniversities(filtered);
    } else {
      setFilteredUniversities(universities);
    }
  }, [searchText, universities]);

  const loadUniversities = async () => {
    try {
      const data = await universityService.getUniversities();
      setUniversities(data);
      setFilteredUniversities(data);
    } catch (error) {
      console.error('Failed to load universities:', error);
      setErrorMessage('학교 목록을 불러오는데 실패했습니다.');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setShowDropdown(true);
    setSelectedUniversityId(0);
  };

  const handleSelectUniversity = (uni: University) => {
    setSelectedUniversityId(uni.id);
    setSearchText(uni.name);
    setShowDropdown(false);
  };

  const clearSearch = () => {
    setSearchText('');
    setSelectedUniversityId(0);
    setShowDropdown(false);
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRequestPasswordVerification = async () => {
    if (!passwordEmail) {
      setPasswordErrorMessage('이메일을 입력해주세요.');
      return;
    }

    setIsRequestingPasswordCode(true);
    setPasswordErrorMessage('');

    try {
      await authService.requestPasswordVerificationCode({ email: passwordEmail });
      alert('인증번호가 발송되었습니다.');
      setPasswordRemainingTime(600); // 10분 = 600초
      setIsPasswordCodeSent(true);
    } catch (error) {
      console.error('인증번호 요청 실패:', error);
      setPasswordErrorMessage('인증번호 요청에 실패했습니다. 이메일을 확인해주세요.');
    } finally {
      setIsRequestingPasswordCode(false);
    }
  };

  const handleVerifyPasswordCode = async () => {
    if (!passwordEmail) {
      setPasswordErrorMessage('이메일을 입력해주세요.');
      return;
    }

    if (!passwordVerificationCode) {
      setPasswordErrorMessage('인증번호를 입력해주세요.');
      return;
    }

    if (!isPasswordCodeSent) {
      setPasswordErrorMessage('인증번호를 먼저 요청해주세요.');
      return;
    }

    setIsVerifyingPassword(true);
    setPasswordErrorMessage('');

    try {
      const response = await authService.verifyPasswordCode({
        email: passwordEmail,
        code: passwordVerificationCode
      });

      // 인증 성공 시 비밀번호 재설정 페이지로 이동
      navigate('/reset-password', {
        state: {
          email: passwordEmail,
          verificationToken: response.verificationToken
        }
      });
    } catch (error) {
      console.error('인증번호 확인 실패:', error);
      setPasswordErrorMessage('인증번호가 올바르지 않습니다. 다시 확인해주세요.');
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const handleFindEmail = async () => {
    setErrorMessage('');

    if (!selectedUniversityId || selectedUniversityId === 0) {
      setErrorMessage('학교를 선택해주세요.');
      return;
    }

    if (!studentId.trim()) {
      setErrorMessage('학번을 입력해주세요.');
      return;
    }

    if (!name.trim()) {
      setErrorMessage('이름을 입력해주세요.');
      return;
    }

    try {
      const result = await memberService.findEmail({
        universityId: selectedUniversityId,
        studentId: studentId.trim(),
        name: name.trim()
      });

      // 결과 페이지로 이동
      navigate('/search-email-result', {
        state: {
          email: result.email,
          createdAt: result.createdAt
        }
      });
    } catch (error: any) {
      console.error('Failed to find email:', error);
      if (error.response?.status === 404) {
        setErrorMessage('일치하는 회원 정보를 찾을 수 없습니다.');
      } else {
        setErrorMessage('이메일 찾기에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const isFormValid = selectedUniversityId > 0 && studentId.trim() && name.trim();
  const isPasswordFormValid = passwordEmail.trim() && passwordVerificationCode.trim() && isPasswordCodeSent;

  return (
    <S.Page>
      <S.Header>아이디/비밀번호 찾기</S.Header>

      <S.TabContainer>
        <S.Tab
          active={activeTab === 'email'}
          onClick={() => setActiveTab('email')}
        >
          아이디 찾기
        </S.Tab>
        <S.Tab
          active={activeTab === 'password'}
          onClick={() => setActiveTab('password')}
        >
          비밀번호 찾기
        </S.Tab>
      </S.TabContainer>

      {activeTab === 'email' && (
        <S.ContentWrap>
          <div>
            <S.SectionTitle>학교</S.SectionTitle>
            <S.SearchContainer ref={dropdownRef}>
              <S.InputContainer>
                <S.Input
                  placeholder="대학교를 검색해주세요"
                  value={searchText}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                />
                {searchText && (
                  <S.ClearButton onClick={clearSearch}>
                    <S.ClearIcon>×</S.ClearIcon>
                  </S.ClearButton>
                )}
              </S.InputContainer>
              {showDropdown && filteredUniversities.length > 0 && (
                <S.DropdownList>
                  {filteredUniversities.map((uni) => (
                    <S.DropdownItem
                      key={uni.id}
                      onClick={() => handleSelectUniversity(uni)}
                      selected={uni.id === selectedUniversityId}
                    >
                      {uni.name}
                    </S.DropdownItem>
                  ))}
                </S.DropdownList>
              )}
              {showDropdown && searchText && filteredUniversities.length === 0 && (
                <S.DropdownList>
                  <S.NoResult>검색 결과가 없습니다</S.NoResult>
                </S.DropdownList>
              )}
            </S.SearchContainer>

            <S.SectionTitle style={{ marginTop: '20px' }}>학번</S.SectionTitle>
            <S.InputGroup>
              <S.InputWrap>
                <input
                  type="text"
                  placeholder="학번"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                />
              </S.InputWrap>
            </S.InputGroup>

            <S.SectionTitle style={{ marginTop: '20px' }}>이름</S.SectionTitle>
            <S.InputGroup>
              <S.InputWrap>
                <input
                  type="text"
                  placeholder="이름"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </S.InputWrap>
            </S.InputGroup>

            {errorMessage && <S.ErrorMessage>{errorMessage}</S.ErrorMessage>}
          </div>
        </S.ContentWrap>
      )}

      {activeTab === 'password' && (
        <S.ContentWrap>
          <div>
            <S.SectionTitle>학교이메일</S.SectionTitle>

            <S.EmailContainer>
              <S.Input
                placeholder="이메일"
                value={passwordEmail}
                onChange={(e) => setPasswordEmail(e.target.value)}
                style={{ flex: 1 }}
              />
              <S.VerifyButton
                onClick={handleRequestPasswordVerification}
                disabled={isRequestingPasswordCode}
              >
                {isRequestingPasswordCode ? '전송 중...' : isPasswordCodeSent ? '재전송' : '인증요청'}
              </S.VerifyButton>
            </S.EmailContainer>

            <S.InputContainer style={{ marginTop: '12px' }}>
              <S.Input
                placeholder="인증번호 입력"
                value={passwordVerificationCode}
                onChange={(e) => setPasswordVerificationCode(e.target.value)}
                style={{ paddingRight: isPasswordCodeSent ? '80px' : '48px' }}
              />
              {isPasswordCodeSent && passwordRemainingTime > 0 && (
                <S.Timer>{formatTime(passwordRemainingTime)}</S.Timer>
              )}
              {isPasswordCodeSent && passwordRemainingTime === 0 && (
                <S.TimerExpired>시간만료</S.TimerExpired>
              )}
            </S.InputContainer>

            {passwordErrorMessage && <S.ErrorMessage>{passwordErrorMessage}</S.ErrorMessage>}
          </div>
        </S.ContentWrap>
      )}

      <S.BottomButton
        className={
          (activeTab === 'email' && isFormValid) || (activeTab === 'password' && isPasswordFormValid)
            ? 'active'
            : ''
        }
        onClick={activeTab === 'email' ? handleFindEmail : handleVerifyPasswordCode}
        disabled={
          isVerifyingPassword ||
          (activeTab === 'email' && !isFormValid) ||
          (activeTab === 'password' && !isPasswordFormValid)
        }
      >
        {isVerifyingPassword ? '확인 중...' : '확인'}
      </S.BottomButton>
    </S.Page>
  );
}

export default SearchMain;
